import { Document, Font, Page, Text, View } from '@react-pdf/renderer';
import { DateTime } from 'luxon';

import {
  DispatchAssignmentRead,
  DispatchTripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { commonStyles } from 'src/common/pdf/common.styles';
import { formatFullName } from 'src/common/util/formatFullName';

// Support for emojis, see: https://react-pdf.org/fonts
Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/',
});

// Avoid hyphenations
Font.registerHyphenationCallback((word) => [word]);

interface DispatchCalendarPDFProps {
  selectedDate: string;
  assignments: DispatchAssignmentRead[];
  tripsByAssignmentId: Record<string, DispatchTripRead[]>;
}

export function DispatchCalendarPDF({
  selectedDate,
  assignments,
  tripsByAssignmentId,
}: DispatchCalendarPDFProps) {
  const formattedDate = DateTime.fromISO(selectedDate).toLocaleString(
    DateTime.DATE_FULL
  );

  return (
    <Document>
      {assignments.map((assignment) => {
        const trips = tripsByAssignmentId[assignment.id] ?? [];
        const driverName = `${assignment.driver.first_name} ${assignment.driver.last_name}`;
        const attendantNames = assignment.attendants
          .map((a) => formatFullName(a))
          .join(', ');

        return (
          <Page key={assignment.id} size="A4" style={commonStyles.page}>
            {/* Header */}
            <View style={commonStyles.vstack}>
              <View style={commonStyles.hstack}>
                <Text style={commonStyles.strong}>{formattedDate}</Text>
                <Text>{driverName}</Text>
                <Text>{assignment.vehicle.nickname}</Text>
              </View>
              {attendantNames ? (
                <View style={commonStyles.hstack}>
                  <Text style={commonStyles.strong}>Attendants:</Text>
                  <Text>{attendantNames}</Text>
                </View>
              ) : null}
            </View>

            <View style={commonStyles.separatorMd} />

            {/* Trip list */}
            <View style={commonStyles.vstack}>
              {trips.map((trip, index) => (
                <View key={trip.id} wrap={false}>
                  {index > 0 ? <View style={commonStyles.separatorSm} /> : null}
                  <View style={commonStyles.vstack}>
                    {/* Passenger + pickup time */}
                    <View style={commonStyles.hstack}>
                      <Text style={commonStyles.strong}>
                        {trip.passenger.full_name}
                      </Text>
                    </View>
                    <View style={commonStyles.hstack}>
                      <Text style={commonStyles.strong}>Pickup Time:</Text>
                      <Text>
                        {DateTime.fromISO(
                          trip.scheduled_pickup_at
                        ).toLocaleString(DateTime.TIME_SIMPLE)}
                      </Text>
                    </View>
                    {trip.appointment_at ? (
                      <View style={commonStyles.hstack}>
                        <Text style={commonStyles.strong}>
                          Appointment Time:
                        </Text>
                        <Text>
                          {DateTime.fromISO(trip.appointment_at).toLocaleString(
                            DateTime.TIME_SIMPLE
                          )}
                        </Text>
                      </View>
                    ) : null}
                    {/* Pickup address */}
                    <View style={commonStyles.hstack}>
                      <Text style={commonStyles.strong}>Pickup Address:</Text>
                      <Text>{trip.pickup_address}</Text>
                    </View>
                    {/* Dropoff address */}
                    <View style={commonStyles.hstack}>
                      <Text style={commonStyles.strong}>Dropoff Address:</Text>
                      <Text>{trip.dropoff_address}</Text>
                    </View>
                    {/* Service type + space type */}
                    <View style={commonStyles.hstack}>
                      <Text style={commonStyles.strong}>Service Type:</Text>
                      <Text>{trip.service_type}</Text>
                      <Text style={commonStyles.strong}>Space Type:</Text>
                      <Text>{trip.space_type}</Text>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </Page>
        );
      })}
    </Document>
  );
}
